import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "../../lib/utils"
import { useTheme } from "../../styles/ThemeProvider"

const alertVariants = cva(
  "relative w-full [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4",
  {
    variants: {
      variant: {
        default: "",
        destructive: "",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.forwardRef(({ className, variant = "default", ...props }, ref) => {
  const { themeObject } = useTheme();
  
  // Obtener tokens del tema actual
  const alertTokens = themeObject.components.alert || {};
  const variantStyles = alertTokens.variants?.[variant] || {};
  
  const alertStyles = {
    padding: alertTokens.padding || themeObject.spacing?.md || '16px',
    borderRadius: alertTokens.borderRadius || themeObject.borderRadius?.md || '8px',
    border: `1px solid ${variantStyles.borderColor || themeObject.colors.border.default}`,
    backgroundColor: variantStyles.backgroundColor || themeObject.colors.surface.default,
    color: variantStyles.color || themeObject.colors.text.primary,
    width: '100%',
  };

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      style={alertStyles}
      {...props}
    />
  );
})
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef(({ className, ...props }, ref) => {
  const { themeObject } = useTheme();
  
  const titleStyles = {
    marginBottom: themeObject.spacing?.xs || '4px',
    fontWeight: themeObject.typography?.fontWeight?.medium || '500',
    lineHeight: themeObject.typography?.lineHeight?.none || '1',
    letterSpacing: themeObject.typography?.letterSpacing?.tight || '-0.025em',
    color: themeObject.colors.text.primary,
  };

  return (
    <h5
      ref={ref}
      className={cn("", className)}
      style={titleStyles}
      {...props}
    />
  );
})
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => {
  const { themeObject } = useTheme();
  
  const descriptionStyles = {
    fontSize: themeObject.typography?.fontSize?.sm || '14px',
    color: themeObject.colors.text.secondary,
    lineHeight: themeObject.typography?.lineHeight?.relaxed || '1.625',
  };

  return (
    <div
      ref={ref}
      className={cn("[&_p]:leading-relaxed", className)}
      style={descriptionStyles}
      {...props}
    />
  );
})
AlertDescription.displayName = "AlertDescription"

export { 
  Alert, 
  AlertTitle,       // Make sure AlertTitle is exported
  AlertDescription 
}